import { router } from '@inertiajs/react'
import AppLayout from '@/components/layouts/AppLayout'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardActions from '@mui/material/CardActions'
import Grid from '@mui/material/Grid'
import Avatar from '@mui/material/Avatar'
import Chip from '@mui/material/Chip'
import AddIcon from '@mui/icons-material/Add'
import BusinessIcon from '@mui/icons-material/Business'
import type { Vendor } from '@/types'

interface Props {
  vendors: Vendor[]
}

export default function VendorsIndex({ vendors }: Props) {
  return (
    <AppLayout title="My Vendors">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Vendor Organizations</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => router.visit('/vendors/new')}
        >
          Create Vendor
        </Button>
      </Box>

      {vendors.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No vendor organizations yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a vendor organization to start publishing app entries.
          </Typography>
          <Button variant="contained" onClick={() => router.visit('/vendors/new')}>
            Create Your First Vendor
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {vendors.map((vendor) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={vendor.id}>
              <Card elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={vendor.logo_url || undefined}
                      sx={{ width: 48, height: 48, mr: 2, bgcolor: 'primary.main' }}
                    >
                      <BusinessIcon />
                    </Avatar>
                    <Typography variant="h6" noWrap>{vendor.name}</Typography>
                  </Box>
                  {vendor.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {vendor.description.substring(0, 120)}
                      {vendor.description.length > 120 ? '...' : ''}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip label={`${vendor.members_count} members`} size="small" variant="outlined" />
                    <Chip label={`${vendor.entries_count} entries`} size="small" variant="outlined" />
                  </Box>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => router.visit(`/vendors/${vendor.slug}`)}>
                    Dashboard
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </AppLayout>
  )
}
